import React, { Fragment, useEffect, useState } from 'react';
import BeneficialOwners, { BeneficialOwnerData } from '../../BeneficialOwners';
import { OrgNode } from '../../../services/orgs/interfaces';
import RequiredStatement from '../RequiredStatement';
import { FieldError } from '../../VgsInput/field';
import { ShowPayable } from '../../VaultForm';
import RequiredAsterisk from '../../RequiredAsterisk';
import KycConfirmation from '../../KycConfirmation';
import { BUSINESS_STRUCTURES_THAT_REQUIRE_OWNERS } from '../../../const';
import KycBioInput from '../../KycBioInput';

export default function Kyc({
  businessStructure,
  onValidChange,
  onRepresentativeTitleChange,
  onRepresentativeIsOwnerChange,
  onRepresentativePercentOwnershipChange,
  onRepresentativeAddressRegionChange,
  onBeneficialOwnersChange,
  onSaveToPayableOrgChange,
  form,
  showSavePayableCheckbox,
  payableOrg,
  fieldErrors,
  userKyc,
  token,
  onRevealFail,
  onRevealSuccess,
}: {
  businessStructure: string;
  onValidChange: (isValid: boolean) => void;
  onRepresentativeTitleChange: (title: string) => void;
  onRepresentativeAddressRegionChange: (region: string) => void;
  onRepresentativeIsOwnerChange: (isOwner: boolean) => void;
  onRepresentativePercentOwnershipChange: (percentOwnership: number) => void;
  onBeneficialOwnersChange: (
    beneficialOwnersData: BeneficialOwnerData[],
  ) => void;
  onSaveToPayableOrgChange: (savePayableOrg: boolean) => void;
  form: VGSForm<{
    vaultCreateKybKycSubmit: VaultFormMutationResponse;
  }>;
  showSavePayableCheckbox: ShowPayable;
  payableOrg?: OrgNode;
  fieldErrors: FieldError[];
  userKyc?: Kyc;
  token: string;
  onRevealFail: () => void;
  onRevealSuccess: () => void;
}): React.JSX.Element {
  const [isValid, setIsValid] = useState(false);
  const [representativeIsValid, setRepresentativeIsValid] = useState(false);
  const [beneficialOwnersAreValid, setBeneficialOwnersAreValid] =
    useState(true);
  const [saveToPayableOrg, setSaveToPayableOrg] = useState(false);
  const askIsOwner =
    BUSINESS_STRUCTURES_THAT_REQUIRE_OWNERS.includes(businessStructure);

  useEffect(() => {
    const newValidity =
      (representativeIsValid &&
        beneficialOwnersAreValid &&
        // showSavePayableCheckbox is required, check that it has been set to true
        (showSavePayableCheckbox === 'REQUIRED' ? saveToPayableOrg : true)) ||
      // if userKyc is provided there are no fields to edit so the form becomes valid
      userKyc !== undefined;
    if (newValidity !== isValid) {
      setIsValid(newValidity);
      onValidChange(newValidity);
    }
  }, [
    beneficialOwnersAreValid,
    isValid,
    onValidChange,
    representativeIsValid,
    saveToPayableOrg,
    showSavePayableCheckbox,
    userKyc,
  ]);

  useEffect(() => {
    if (userKyc === undefined) {
      onRevealSuccess();
    }
  }, [onRevealSuccess, userKyc]);

  return (
    <Fragment>
      <div className='md:flex mb-5'>
        <div className='md:w-full my-2'>
          <h1 className='page-title'>Personal Details</h1>
          <p className='page-text-primary'>
            A controller is required for every fundraiser to ensure we adhere to
            all anti-money laundering laws. Our payment partner will securely
            validate your details.
          </p>
          {/* don't show when required statement when user cannot edit kyc */}
          {userKyc === undefined && <RequiredStatement />}
        </div>
      </div>
      {userKyc !== undefined ? (
        <KycConfirmation
          kyc={userKyc}
          token={token}
          onRevealSuccess={onRevealSuccess}
          onRevealFail={onRevealFail}
        />
      ) : (
        <KycBioInput
          entityName='representative'
          onAddressRegionChange={onRepresentativeAddressRegionChange}
          onTitleChange={onRepresentativeTitleChange}
          onIsOwnerChange={onRepresentativeIsOwnerChange}
          onPercentOwnershipChange={onRepresentativePercentOwnershipChange}
          onValidChange={(isValid): void => {
            setRepresentativeIsValid(isValid);
          }}
          form={form}
          askIsOwner={askIsOwner}
          askPercentOwnershipOnLoad={false}
          askTitle={true}
          fieldErrors={fieldErrors}
        />
      )}

      {askIsOwner && (
        <BeneficialOwners
          onValidChange={(isValid): void => {
            setBeneficialOwnersAreValid(isValid);
          }}
          onChange={onBeneficialOwnersChange}
          form={form}
          fieldErrors={fieldErrors}
        />
      )}
      <div className='md:flex mb-5'>
        <div className='md:w-full my-2'>
          <div className='page-text-alert'>
            <p>
              As a reminder, your details must match government records. Please
              ensure your name, DOB & Address are a 100% match to your driver’s
              license.
            </p>
          </div>
        </div>
      </div>
      {showSavePayableCheckbox !== 'HIDE' && (
        <div className='flex flex-row items-start'>
          <input
            type='checkbox'
            className='m-2'
            onChange={(e): void => {
              if (e.target.checked !== saveToPayableOrg) {
                setSaveToPayableOrg(e.target.checked);
                onSaveToPayableOrgChange(e.target.checked);
              }
            }}
          />
          <p className='basis-7/8'>
            By checking the box and clicking Submit for Review I am authorizing
            the use of the above information by teams and programs associated
            with {payableOrg?.name}.{' '}
            {showSavePayableCheckbox === 'REQUIRED' && <RequiredAsterisk />}
          </p>
        </div>
      )}
    </Fragment>
  );
}
